// ✅ frontend/src/pages/LegalDocForm.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api";

function LegalDocForm() {
  const { type } = useParams();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewHTML, setPreviewHTML] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [stampImage, setStampImage] = useState(null);

  // ✅ تحسين المعاينة على الموبايل (حل مشاكل اللخبطة/السكرول/الزوم)
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // 🏷️ خريطة تحويل أسماء العقود من الإنجليزية إلى العربية
  const arabicNames = {
    finalSale: "عقد بيع نهائي",
    preliminarySale: "عقد بيع ابتدائي",
    officialRent: "عقد إيجار موثق",
    division: "عقد قسمة",
    generalPower: "توكيل رسمي عام",
    specialPower: "توكيل رسمي خاص",
    donation: "إقرار تبرع",
    waiver: "إقرار تنازل",
    mortgage: "عقد رهن",
    reconciliation: "محضر تصالح",
    civilCase: "صحيفة دعوى مدنية",
    warning: "إنذار قضائي",
    appeal: "صحيفة استئناف",
    cassation: "صحيفة طعن بالنقض",
    criminalComplaint: "صحيفة جنحة مباشرة",
    defenseMemo: "مذكرة دفاع",
    executionNotice: "صحيفة تنفيذ حكم",
    companyEstablishment: "عقد تأسيس شركة",
    partnership: "عقد شراكة",
    commissionAgency: "عقد وكالة بالعمولة",
    consulting: "عقد خدمات استشارية",
    nda: "اتفاقية عدم إفشاء",
    contracting: "عقد مقاولة",
  };

  // ===================== التعديل (بدون تغيير أي شيء عندك) =====================
  const responsiveContainer =
    "w-full max-w-lg sm:max-w-2xl flex flex-col space-y-3";

  // ✅ ستايل معاينة ثابت ومرن + سكرول محترم على الموبايل
  const previewStyle = useMemo(
    () => ({
      width: "100%",
      minHeight: isMobile ? "50vh" : "70vh",
      maxHeight: isMobile ? "60vh" : "80vh",
      padding: isMobile ? "12px" : "15px",
      overflowY: "auto",
      overflowX: "hidden",
      WebkitOverflowScrolling: "touch",
      overscrollBehavior: "contain",
      border: "1px solid #ddd",
      borderRadius: "12px",
      direction: "rtl",
      fontFamily: "Cairo, sans-serif",
      fontSize: isMobile ? "16px" : "18px",
      lineHeight: "1.8",
      background: "white",
      wordBreak: "break-word",
      whiteSpace: "normal",
      outline: "none",
      touchAction: "manipulation",
    }),
    [isMobile]
  );
  // ==============================================================================

  // ✅ تحديث بيانات أي حقل
  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // ✅ دالة معاينة العقد (عرض مباشر)
  const handlePreview = async () => {
    try {
      const res = await api.post(`/legal-docs/preview/${type}`, form, {
        headers: { "Content-Type": "application/json" },
      });

      // بعض الباك إند بيرجع string وبعضهم بيرجع {html: "..."}
      const html = typeof res.data === "string" ? res.data : res.data?.html || "";
      setPreviewHTML(html);
      setShowPreview(true);

      // ✅ على الموبايل: نزّل المستخدم مباشرة للمعاينة (بدون لخبطة)
      setTimeout(() => {
        const el = document.getElementById("previewSection");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch (err) {
      console.error("❌ Error generating preview:", err);
      alert("حدث خطأ أثناء معاينة العقد");
    }
  };

  // ✅ دالة إنشاء وتحميل PDF
  const handleSubmit = async () => {
    try {
      setLoading(true);

      const editableDiv = document.getElementById("editablePreview");

      // ✅ مهم: متعدّليش على state object مباشرة
      const payload = {
        ...form,
        customEditedHTML: editableDiv ? editableDiv.innerHTML : undefined,
      };

      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));

      if (stampImage) {
        formData.append("stampImage", stampImage);
      }

      const res = await api.post(`/legal-docs/generate/${type}`, formData, {
        responseType: "blob",
        headers: { "Content-Type": "multipart/form-data" },
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${arabicNames[type] || type}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("❌ Error creating PDF:", err);
      alert("حدث خطأ أثناء إنشاء المستند");
    } finally {
      setLoading(false);
    }
  };

  // ✅ جميع العقود القانونية (بتفاصيل كاملة ومنظمة)
  const fieldsByType = {
    // 📜 عقد بيع نهائي
    finalSale: [
      ["contractDate", "📅 تاريخ العقد"],
      ["seller", "👤 اسم البائع"],
      ["sellerId", "🪪 رقم بطاقة البائع"],
      ["sellerIssuedFrom", "📍 جهة إصدار بطاقة البائع"],
      ["sellerAddress", "🏠 عنوان البائع"],
      ["buyer", "👤 اسم المشتري"],
      ["buyerId", "🪪 رقم بطاقة المشتري"],
      ["buyerIssuedFrom", "📍 جهة إصدار بطاقة المشتري"],
      ["buyerAddress", "🏠 عنوان المشتري"],
      ["property", "🏢 بيانات العقار أو الأرض"],
      ["propertyDetails", "📖 تفاصيل إضافية عن العقار"],
      ["price", "💰 قيمة البيع (رقم)"],
      ["priceWords", "💬 قيمة البيع كتابة"],
      ["paymentTerms", "📑 شروط الدفع"],
      ["agreementTerms", "📄 البنود الأساسية للعقد"],
      ["witness1", "🖊️ اسم الشاهد الأول"],
      ["witness1Id", "🪪 رقم بطاقة الشاهد الأول"],
      ["witness1Sign", "✍️ توقيع الشاهد الأول"],
      ["witness2", "🖊️ اسم الشاهد الثاني"],
      ["witness2Id", "🪪 رقم بطاقة الشاهد الثاني"],
      ["witness2Sign", "✍️ توقيع الشاهد الثاني"],
    ],

    // 📜 عقد بيع ابتدائي
    preliminarySale: [
      ["contractDate", "📅 تاريخ العقد"],
      ["seller", "اسم البائع"],
      ["buyer", "اسم المشتري"],
      ["property", "بيانات العقار"],
      ["price", "قيمة البيع"],
      ["priceWords", "المبلغ كتابة"],
      ["deliveryDate", "تاريخ التسليم"],
      ["paymentTerms", "شروط السداد"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🏠 عقد إيجار موثق
    officialRent: [
      ["contractDate", "📅 تاريخ العقد"],
      ["landlord", "اسم المؤجر"],
      ["landlordId", "رقم بطاقة المؤجر"],
      ["landlordAddress", "عنوان المؤجر"],
      ["tenant", "اسم المستأجر"],
      ["tenantId", "رقم بطاقة المستأجر"],
      ["tenantAddress", "عنوان المستأجر"],
      ["property", "بيانات العقار المؤجر"],
      ["duration", "مدة الإيجار (بالشهور)"],
      ["rentValue", "قيمة الإيجار الشهري"],
      ["paymentTerms", "شروط الدفع"],
      ["notaryOffice", "مكتب التوثيق"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🧩 عقد قسمة
    division: [
      ["contractDate", "📅 تاريخ القسمة"],
      ["partyOne", "اسم الطرف الأول"],
      ["partyOneId", "رقم بطاقة الطرف الأول"],
      ["partyTwo", "اسم الطرف الثاني"],
      ["partyTwoId", "رقم بطاقة الطرف الثاني"],
      ["property", "العقار محل القسمة"],
      ["shareOne", "نصيب الطرف الأول"],
      ["shareTwo", "نصيب الطرف الثاني"],
      ["divisionType", "نوع القسمة (ودية / قضائية)"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🖋️ توكيل رسمي عام
    generalPower: [
      ["contractDate", "📅 تاريخ التوكيل"],
      ["principal", "اسم الموكل"],
      ["principalId", "رقم بطاقة الموكل"],
      ["principalAddress", "عنوان الموكل"],
      ["agent", "اسم الوكيل"],
      ["agentId", "رقم بطاقة الوكيل"],
      ["agentAddress", "عنوان الوكيل"],
      ["purpose", "موضوع التوكيل (بيع / شراء / إدارة...)"],
      ["duration", "مدة سريان التوكيل (إن وجدت)"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🖋️ توكيل رسمي خاص
    specialPower: [
      ["contractDate", "📅 تاريخ التوكيل"],
      ["principal", "اسم الموكل"],
      ["principalId", "رقم بطاقة الموكل"],
      ["agent", "اسم الوكيل"],
      ["agentId", "رقم بطاقة الوكيل"],
      ["purpose", "موضوع التوكيل (بيع / إدارة / تقاضي / استلام...)"],
      ["property", "بيانات العقار أو الغرض موضوع التوكيل"],
      ["duration", "مدة التوكيل"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // ❤️ إقرار تبرع
    donation: [
      ["contractDate", "📅 تاريخ الإقرار"],
      ["donor", "اسم المتبرع"],
      ["donorId", "رقم بطاقة المتبرع"],
      ["donee", "اسم المتبرع له"],
      ["doneeId", "رقم بطاقة المتبرع له"],
      ["property", "موضوع التبرع"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // ↔️ إقرار تنازل
    waiver: [
      ["contractDate", "📅 تاريخ التنازل"],
      ["partyOne", "اسم المتنازل"],
      ["partyOneId", "رقم بطاقة المتنازل"],
      ["partyTwo", "اسم المتنازل له"],
      ["partyTwoId", "رقم بطاقة المتنازل له"],
      ["subject", "موضوع التنازل"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🏦 عقد رهن
    mortgage: [
      ["contractDate", "📅 تاريخ العقد"],
      ["mortgagor", "اسم الراهن"],
      ["mortgagorId", "رقم بطاقة الراهن"],
      ["mortgagee", "اسم المرتهن"],
      ["mortgageeId", "رقم بطاقة المرتهن"],
      ["property", "العقار محل الرهن"],
      ["debtAmount", "قيمة الدين"],
      ["duration", "مدة الرهن"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 🤝 محضر تصالح
    reconciliation: [
      ["contractDate", "📅 تاريخ المحضر"],
      ["partyOne", "الطرف الأول"],
      ["partyTwo", "الطرف الثاني"],
      ["subject", "موضوع النزاع"],
      ["agreement", "نص اتفاق التصالح"],
      ["lawyerName", "اسم المحامي المشرف"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
    ],

    // ⚖️ صحيفة دعوى مدنية
    civilCase: [
      ["courtName", "اسم المحكمة"],
      ["plaintiff", "اسم المدعي"],
      ["plaintiffAddress", "عنوان المدعي"],
      ["defendant", "اسم المدعى عليه"],
      ["defendantAddress", "عنوان المدعى عليه"],
      ["subject", "موضوع الدعوى"],
      ["facts", "الوقائع"],
      ["requests", "الطلبات"],
    ],

    // ⚖️ إنذار قضائي
    warning: [
      ["contractDate", "📅 تاريخ الإنذار"],
      ["sender", "اسم المنذر"],
      ["senderAddress", "عنوان المنذر"],
      ["senderId", "رقم بطاقة المنذر"],
      ["receiver", "اسم المنذر إليه"],
      ["receiverAddress", "عنوان المنذر إليه"],
      ["receiverId", "رقم بطاقة المنذر إليه"],
      ["subject", "موضوع الإنذار"],
      ["details", "نص الإنذار"],
      ["period", "مدة التنفيذ (بالأيام)"],
      ["lawyerName", "اسم المحامي القائم بالإنذار"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // ⚖️ صحيفة استئناف
    appeal: [
      ["contractDate", "📅 تاريخ الصحيفة"],
      ["courtName", "اسم محكمة الاستئناف"],
      ["appellant", "اسم المستأنف"],
      ["appellantAddress", "عنوان المستأنف"],
      ["appellantId", "رقم بطاقة المستأنف"],
      ["respondent", "اسم المستأنف ضده"],
      ["respondentAddress", "عنوان المستأنف ضده"],
      ["respondentId", "رقم بطاقة المستأنف ضده"],
      ["caseNumber", "رقم القضية"],
      ["judgmentDate", "تاريخ الحكم المستأنف"],
      ["reasons", "أسباب الاستئناف"],
      ["requests", "طلبات المستأنف"],
    ],

    // ⚖️ صحيفة طعن بالنقض
    cassation: [
      ["contractDate", "📅 تاريخ الصحيفة"],
      ["appellant", "اسم الطاعن"],
      ["appellantAddress", "عنوان الطاعن"],
      ["appellantId", "رقم بطاقة الطاعن"],
      ["respondent", "اسم المطعون ضده"],
      ["respondentAddress", "عنوان المطعون ضده"],
      ["respondentId", "رقم بطاقة المطعون ضده"],
      ["caseNumber", "رقم القضية"],
      ["judgmentDate", "تاريخ الحكم المطعون فيه"],
      ["reasons", "أسباب الطعن"],
      ["requests", "طلبات الطعن"],
    ],

    // ⚖️ صحيفة جنحة مباشرة
    criminalComplaint: [
      ["contractDate", "📅 تاريخ الصحيفة"],
      ["plaintiff", "اسم المدعي بالحق المدني"],
      ["plaintiffAddress", "عنوان المدعي"],
      ["plaintiffId", "رقم بطاقة المدعي"],
      ["defendant", "اسم المتهم"],
      ["defendantAddress", "عنوان المتهم"],
      ["defendantId", "رقم بطاقة المتهم"],
      ["subject", "موضوع الجنحة"],
      ["facts", "الوقائع"],
      ["requests", "الطلبات"],
    ],

    // ⚖️ مذكرة دفاع
    defenseMemo: [
      ["contractDate", "📅 تاريخ المذكرة"],
      ["lawyer", "اسم المحامي مقدم المذكرة"],
      ["client", "اسم الموكل"],
      ["caseNumber", "رقم القضية"],
      ["year", "السنة"],
      ["court", "اسم المحكمة"],
      ["proceduralDefenses", "الدفوع الشكلية"],
      ["substantiveDefenses", "الدفوع الموضوعية"],
      ["finalRequests", "الطلبات الختامية"],
      ["signature", "توقيع المحامي"],
    ],

    // ⚖️ صحيفة تنفيذ حكم
    executionNotice: [
      ["contractDate", "📅 تاريخ الصحيفة"],
      ["applicant", "اسم مقدم الطلب"],
      ["applicantAddress", "عنوان مقدم الطلب"],
      ["applicantId", "رقم بطاقة مقدم الطلب"],
      ["respondent", "اسم المنفذ ضده"],
      ["respondentAddress", "عنوان المنفذ ضده"],
      ["respondentId", "رقم بطاقة المنفذ ضده"],
      ["judgmentNumber", "رقم الحكم"],
      ["judgmentDate", "تاريخ الحكم"],
      ["court", "المحكمة الصادر منها الحكم"],
      ["subject", "موضوع التنفيذ"],
      ["requests", "طلبات التنفيذ"],
    ],

    // 💼 عقد تأسيس شركة
    companyEstablishment: [
      ["contractDate", "📅 تاريخ العقد"],
      ["companyName", "اسم الشركة"],
      ["companyType", "نوع الشركة (توصية بسيطة / مساهمة / تضامن...)"],
      ["partnerOne", "اسم الشريك الأول"],
      ["partnerOneId", "رقم بطاقة الشريك الأول"],
      ["partnerTwo", "اسم الشريك الثاني"],
      ["partnerTwoId", "رقم بطاقة الشريك الثاني"],
      ["purpose", "غرض الشركة"],
      ["capital", "رأس المال الكلي"],
      ["shareOne", "نسبة الشريك الأول %"],
      ["shareTwo", "نسبة الشريك الثاني %"],
      ["duration", "مدة الشركة (بالسنوات)"],
      ["companyAddress", "عنوان المقر الرئيسي"],
      ["managerName", "اسم المدير"],
      ["managerAuthority", "صلاحيات المدير"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 💼 عقد شراكة
    partnership: [
      ["contractDate", "📅 تاريخ العقد"],
      ["partnerOne", "اسم الشريك الأول"],
      ["partnerOneId", "رقم بطاقة الشريك الأول"],
      ["partnerTwo", "اسم الشريك الثاني"],
      ["partnerTwoId", "رقم بطاقة الشريك الثاني"],
      ["purpose", "غرض الشراكة"],
      ["capital", "رأس المال الكلي"],
      ["shareOne", "نسبة الشريك الأول %"],
      ["shareTwo", "نسبة الشريك الثاني %"],
      ["profitDistribution", "نظام توزيع الأرباح والخسائر"],
      ["duration", "مدة العقد (سنوات)"],
      ["terminationConditions", "شروط إنهاء الشراكة"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness1Id", "رقم بطاقة الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
      ["witness2Id", "رقم بطاقة الشاهد الثاني"],
    ],

    // 💼 عقد وكالة بالعمولة
    commissionAgency: [
      ["contractDate", "📅 تاريخ العقد"],
      ["principal", "اسم الأصيل"],
      ["principalId", "رقم بطاقة الأصيل"],
      ["agent", "اسم الوكيل"],
      ["agentId", "رقم بطاقة الوكيل"],
      ["products", "المنتجات / الخدمات موضوع العقد"],
      ["commission", "نسبة العمولة %"],
      ["duration", "مدة العقد"],
      ["paymentTerms", "شروط العمولة والدفع"],
    ],

    // 💼 عقد خدمات استشارية
    consulting: [
      ["contractDate", "📅 تاريخ العقد"],
      ["client", "اسم العميل"],
      ["clientId", "رقم بطاقة العميل"],
      ["consultant", "اسم المستشار"],
      ["consultantId", "رقم بطاقة المستشار"],
      ["subject", "موضوع العقد"],
      ["fees", "قيمة الأتعاب"],
      ["paymentTerms", "شروط الدفع"],
      ["duration", "مدة العقد"],
    ],

    // 💼 اتفاقية عدم إفشاء (NDA)
    nda: [
      ["contractDate", "📅 تاريخ الاتفاقية"],
      ["partyOne", "الطرف الأول"],
      ["partyOneId", "رقم بطاقة الطرف الأول"],
      ["partyTwo", "الطرف الثاني"],
      ["partyTwoId", "رقم بطاقة الطرف الثاني"],
      ["subject", "موضوع السرية / المعلومات المشمولة"],
      ["duration", "مدة الالتزام بالسرية"],
      ["penalty", "العقوبة عند الإفشاء"],
    ],

    // 🏗️ عقد مقاولة
    contracting: [
      ["contractDate", "📅 تاريخ العقد"],
      ["owner", "اسم المالك"],
      ["ownerId", "رقم بطاقة المالك"],
      ["contractor", "اسم المقاول"],
      ["contractorId", "رقم بطاقة المقاول"],
      ["projectType", "نوع المشروع"],
      ["location", "موقع التنفيذ"],
      ["totalAmount", "قيمة العقد الإجمالية"],
      ["duration", "مدة التنفيذ"],
      ["paymentTerms", "شروط الدفع"],
      ["witness1", "اسم الشاهد الأول"],
      ["witness2", "اسم الشاهد الثاني"],
    ],
  };

  const fields = fieldsByType[type];

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8 flex flex-col items-center">
      <h2 className="text-2xl font-bold mb-6 text-yellow-400 text-center">
        🧾 إعداد {arabicNames[type] || type}
      </h2>

      {!fields ? (
        <p className="text-red-400 text-lg">❌ نوع المستند غير معروف</p>
      ) : (
        <div className={responsiveContainer}>
          {fields.map(([name, label]) => (
            <input
              key={name}
              name={name}
              placeholder={label}
              onChange={handleChange}
              inputMode="text"
              className="p-3 rounded bg-gray-800 border border-gray-700
                         focus:outline-none focus:border-yellow-400
                         text-base w-full"
              style={{
                fontSize: "16px", // ✅ يمنع Zoom تلقائي على iOS
                WebkitTextSizeAdjust: "100%",
              }}
            />
          ))}
        </div>
      )}

      {/* 🖼️ رفع الختم */}
      <input
        type="file"
        id="stampImage"
        name="stampImage"
        accept="image/*"
        onChange={(e) => setStampImage(e.target.files[0])}
        className="mt-4 bg-gray-800 border border-gray-600 text-white
                   p-3 rounded w-full max-w-lg sm:max-w-xl"
        style={{ fontSize: "16px" }} // ✅ نفس الفكرة للموبايل
      />

      {/* 🔍 معاينة */}
      <button
        onClick={handlePreview}
        className="mt-6 bg-blue-500 hover:bg-blue-400 text-white
                   font-bold py-3 px-6 rounded w-full max-w-lg sm:max-w-xl"
      >
        🔍 معاينة العقد
      </button>

      {/* ⬇️ تحميل PDF */}
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="mt-4 bg-yellow-500 hover:bg-yellow-400 text-black
                   font-bold py-3 px-6 rounded w-full max-w-lg sm:max-w-xl disabled:opacity-50"
      >
        {loading ? "⏳ جاري الإنشاء..." : "⬇️ إنشاء وتحميل المستند"}
      </button>

      {/* 🧾 المعاينة */}
      {showPreview && (
        <div
          id="previewSection"
          className="w-full max-w-4xl mt-10 bg-white rounded-lg shadow-lg p-4 sm:p-6 text-black"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold text-gray-800 text-center sm:text-right">
              📄 المعاينة المباشرة للعقد:
            </h3>

            <button
              onClick={() => setShowPreview(false)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-900 font-bold py-2 px-4 rounded"
            >
              ✖️ إغلاق المعاينة
            </button>
          </div>

          <div
            id="editablePreview"
            contentEditable={true}
            suppressContentEditableWarning={true}
            dangerouslySetInnerHTML={{ __html: previewHTML }}
            style={previewStyle}
          ></div>

          <p className="mt-3 text-sm text-gray-600 leading-relaxed">
            ✅ تقدر تعدّل النص هنا مباشرة قبل الحفظ. (المعاينة متظبطة للموبايل: سكرول ناعم + كسر كلمات + مقاسات خط مناسبة)
          </p>
        </div>
      )}
    </div>
  );
}

export default LegalDocForm;
